import _extends from "@babel/runtime/helpers/extends";
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { forwardRef, memo, useCallback, useEffect, useRef, useState } from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { css, jsx } from '@emotion/react';
import { usePlatformLeafEventHandler } from '@atlaskit/analytics-next/usePlatformLeafEventHandler';
import mergeRefs from '@atlaskit/ds-lib/merge-refs';
import { B200 } from '@atlaskit/theme/colors';
import { CheckboxIcon, Label, LabelText, RequiredIndicator } from './internal';
/* eslint-disable @atlaskit/design-system/no-nested-styles */
const checkboxStyles = css({
  width: '100%',
  height: '100%',
  margin: "var(--ds-space-0, 0px)",
  appearance: 'none',
  border: 'none',
  gridArea: '1 / 1 / 2 / 2',
  opacity: 0,
  outline: 'none',
  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
  '& + svg': {
    /**
     *  Change the variables --checkbox-background-color, --checkbox-border-color
     *  and --checkbox-tick-color according to user interactions.
     *  All other variables are constant.
     *  All styles from the input target the sibling svg.
     */
    '--checkbox-background-color': 'var(--local-background)',
    '--checkbox-border-color': 'var(--local-border)',
    '--checkbox-tick-color': 'var(--local-tick-rest)',
    color: 'var(--checkbox-background-color)',
    fill: 'var(--checkbox-tick-color)',
    gridArea: '1 / 1 / 2 / 2',
    pointerEvents: 'none',
    transition: 'color 0.2s ease-in-out, fill 0.2s ease-in-out',
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
    'rect:first-of-type': {
      stroke: 'var(--checkbox-border-color)',
      strokeWidth: "var(--ds-border-width, 1px)",
      transition: 'stroke 0.2s ease-in-out'
    }
  },
  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
  '&&:focus + svg, &&:checked:focus + svg': {
    borderRadius: "var(--ds-border-radius, 0.25rem)",
    outline: `${"var(--ds-border-width-outline, 2px)"} solid ${`var(--ds-border-focused, ${B200})`}`,
    outlineOffset: "var(--ds-space-negative-025, -2px)"
  },
  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
  '&:hover + svg': {
    '--checkbox-background-color': 'var(--local-background-hover)',
    '--checkbox-border-color': 'var(--local-border-hover)'
  },
  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
  '&:checked:hover + svg': {
    '--checkbox-background-color': 'var(--local-background-checked-hover)',
    '--checkbox-border-color': 'var(--local-border-checked-hover)'
  },
  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
  '&:checked + svg': {
    '--checkbox-background-color': 'var(--local-background-checked)',
    '--checkbox-border-color': 'var(--local-border-checked)',
    '--checkbox-tick-color': 'var(--local-tick-checked)'
  },
  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
  '&[data-invalid] + svg': {
    '--checkbox-border-color': 'var(--local-border-invalid)'
  },
  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
  '&:checked[data-invalid] + svg': {
    '--checkbox-border-color': 'var(--local-border-checked-invalid)'
  },
  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
  '&:active + svg': {
    '--checkbox-background-color': 'var(--local-background-active)',
    '--checkbox-border-color': 'var(--local-border-active)'
  },
  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
  '&:checked:active + svg': {
    '--checkbox-background-color': 'var(--local-background-active)',
    '--checkbox-border-color': 'var(--local-border-active)',
    '--checkbox-tick-color': 'var(--local-tick-active)'
  },
  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
  '&:disabled + svg, &:disabled:hover + svg, &:disabled:focus + svg, &:disabled:active + svg, &:disabled[data-invalid] + svg': {
    '--checkbox-background-color': 'var(--local-background-disabled)',
    '--checkbox-border-color': 'var(--local-border-disabled)',
    cursor: 'not-allowed',
    pointerEvents: 'none'
  },
  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
  '&:disabled:checked + svg': {
    '--checkbox-tick-color': 'var(--local-tick-disabled)'
  },
  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-19551
  '&:indeterminate + svg': {
    '--checkbox-background-color': 'var(--local-background-checked)',
    '--checkbox-border-color': 'var(--local-border-checked)',
    '--checkbox-tick-color': 'var(--local-tick-checked)'
  },
  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-19551
  '&:disabled:indeterminate + svg': {
    '--checkbox-background-color': 'var(--local-background-disabled)',
    '--checkbox-border-color': 'var(--local-border-disabled)',
    '--checkbox-tick-color': 'var(--local-tick-disabled)'
  },
  '@media screen and (forced-colors: active)': {
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
    '& + svg': {
      '--checkbox-background-color': 'Canvas',
      '--checkbox-border-color': 'CanvasText',
      '--checkbox-tick-color': 'CanvasText'
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
    '&:checked + svg, &:checked:hover + svg': {
      '--checkbox-background-color': 'Canvas',
      '--checkbox-border-color': 'CanvasText',
      '--checkbox-tick-color': 'CanvasText'
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
    '&:focus + svg rect:first-of-type': {
      stroke: 'Highlight'
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
    '&[data-invalid] + svg': {
      '--checkbox-border-color': 'Highlight'
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
    '&:checked[data-invalid] + svg': {
      '--checkbox-border-color': 'Highlight'
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
    '&:disabled + svg, &:disabled:hover + svg, &:disabled:focus + svg, &:disabled:active + svg, &:disabled[data-invalid] + svg': {
      '--checkbox-background-color': 'Canvas',
      '--checkbox-border-color': 'GrayText',
      '--checkbox-tick-color': 'GrayText'
    }
  }
});
/* eslint-enable @atlaskit/design-system/no-nested-styles */

/**
 * __Checkbox__
 *
 * A checkbox an input control that allows a user to select one or more options from a number of choices.
 *
 * - [Examples](https://atlassian.design/components/checkbox/examples)
 * - [Code](https://atlassian.design/components/checkbox/code)
 * - [Usage](https://atlassian.design/components/checkbox/usage)
 */
const Checkbox = /*#__PURE__*/memo( /*#__PURE__*/forwardRef(function Checkbox(props, ref) {
  const {
    isChecked: isCheckedProp,
    isDisabled = false,
    isInvalid = false,
    defaultChecked = false,
    isIndeterminate = false,
    size = 'medium',
    onChange: onChangeProps,
    analyticsContext,
    label,
    name,
    value,
    isRequired,
    testId,
    xcss,
    ...rest
  } = props;
  const [isCheckedState, setIsCheckedState] = useState(isCheckedProp !== undefined ? isCheckedProp : defaultChecked);
  const onChange = useCallback((e, analyticsEvent) => {
    setIsCheckedState(e.target.checked);
    if (onChangeProps) {
      onChangeProps(e, analyticsEvent);
    }
  }, [onChangeProps]);
  const onChangeAnalytics = usePlatformLeafEventHandler({
    fn: onChange,
    action: 'changed',
    analyticsData: analyticsContext,
    componentName: 'checkbox',
    packageName: "@atlaskit/checkbox",
    packageVersion: "13.7.1"
  });
  const internalRef = useRef(null);
  const mergedRefs = mergeRefs([internalRef, ref]);

  // Use isChecked from the state if it is controlled
  const isChecked = isCheckedProp === undefined ? isCheckedState : isCheckedProp;
  useEffect(() => {
    if (internalRef.current) {
      internalRef.current.indeterminate = isIndeterminate;
    }
  }, [isIndeterminate]);
  return jsx(Label, {
    isDisabled: isDisabled,
    label: label,
    id: rest.id ? `${rest.id}-label` : undefined,
    testId: testId && `${testId}--checkbox-label`
    // Currently the rule hasn't been updated to enable "allowed" dynamic pass-throughs.
    // When there is more usage of this pattern we'll update the lint rule.
    // eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
    ,
    xcss: xcss
  }, jsx("input", _extends({
    // It is necessary only for Safari. It allows to render focus styles.
    tabIndex: 0
  }, rest, {
    type: "checkbox",
    ref: mergedRefs,
    disabled: isDisabled,
    checked: isChecked,
    value: value,
    name: name,
    required: isRequired,
    css: checkboxStyles,
    onChange: onChangeAnalytics,
    "aria-invalid": isInvalid ? 'true' : undefined,
    "data-testid": testId && `${testId}--hidden-checkbox`,
    "data-invalid": isInvalid ? 'true' : undefined
  })), jsx(CheckboxIcon, {
    size: size,
    isIndeterminate: isIndeterminate,
    isChecked: isChecked
  }), label && jsx(LabelText, null, label, isRequired && jsx(RequiredIndicator, null)));
}));
Checkbox.displayName = 'Checkbox';
export default Checkbox;